import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar, Clock, Phone, User, AlertCircle, CheckCircle, Users, RefreshCw, List, CalendarDays, Building2, Home, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import moment from "moment";
import { getTeamCallbacks, getCallbackStats } from "../api/callbackApi";
import { VoiceCallModal } from "../components/leads/VoiceCallModal";
import { CallbackCalendar } from "../components/callbacks/CallbackCalendar";
import { useAuth } from "../hooks/useAuth";
import { fetchUsers } from "../services/crmServices/userService";

export function TeamCallbacks() {
    const { user } = useAuth();
    const [callbacks, setCallbacks] = useState([]);
    const [filteredCallbacks, setFilteredCallbacks] = useState([]);
    const [paginatedCallbacks, setPaginatedCallbacks] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selectedAgent, setSelectedAgent] = useState("all");
    const [agentSearchQuery, setAgentSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [selectedLead, setSelectedLead] = useState(null);
    const [showLeadModal, setShowLeadModal] = useState(false);
    const [viewMode, setViewMode] = useState("list");
    const [agents, setAgents] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [showAgentDropdown, setShowAgentDropdown] = useState(false);
    const ITEMS_PER_PAGE = 10;
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchCallbacks();
        fetchStats();
        fetchAllAgents();
        
        // Auto-refresh every minute
        const interval = setInterval(() => {
            fetchCallbacks();
            fetchStats();
        }, 60000);
        
        return () => clearInterval(interval);
    }, []);

    // Click outside handler for dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowAgentDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        applyFilter();
    }, [filter, selectedAgent, callbacks]);

    useEffect(() => {
        // Update pagination when filtered callbacks change
        setCurrentPage(1);
        updatePagination(filteredCallbacks, 1);
    }, [filteredCallbacks]);

    const fetchAllAgents = async () => {
        try {
            const response = await fetchUsers();
            if (response.status === 200 && response.data) {
                const agentList = response.data
                    .filter(u => u.position && ['agent', 'deskManager', 'floorManager'].includes(u.position.role))
                    .map(u => ({
                        id: u._id,
                        name: `${u.firstName} ${u.lastName}`,
                        role: u.position.role
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                setAgents(agentList);
            }
        } catch (error) {
            console.error("Failed to fetch agents:", error);
        }
    };

    const getSelectedAgentName = () => {
        if (selectedAgent === "all") return "";
        const agent = agents.find(a => a.id === selectedAgent);
        return agent ? agent.name : "";
    };

    const updatePagination = (callbacks, page) => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        setPaginatedCallbacks(callbacks.slice(startIndex, endIndex));
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        updatePagination(filteredCallbacks, newPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const fetchCallbacks = async () => {
        try {
            const response = await getTeamCallbacks();
            if (response.status === 200) {
                setCallbacks(response.data);
            }
        } catch (error) {
            toast.error("Failed to load team callbacks");
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await getCallbackStats();
            if (response.status === 200) {
                setStats(response.data);
            }
        } catch (error) {
            toast.error("Failed to load stats");
        }
    };

    const applyFilter = () => {
        const now = moment();
        let filtered = callbacks.filter(cb => !cb.callbackSchedule.completed);
        
        // Apply time-based filter
        switch (filter) {
            case "overdue":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isBefore(now)
                );
                break;
            case "today":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isSame(now, 'day')
                );
                break;
            case "tomorrow":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isSame(now.clone().add(1, 'day'), 'day')
                );
                break;
            case "week":
                filtered = filtered.filter(cb => 
                    moment(cb.callbackSchedule.scheduledFor).isBetween(
                        now,
                        now.clone().add(7, 'days'),
                        'day',
                        '[]'
                    )
                );
                break;
        }
        
        // Apply agent filter
        if (selectedAgent !== "all") {
            filtered = filtered.filter(cb => cb.assignedUser?._id === selectedAgent);
        }
        
        setFilteredCallbacks(filtered);
    };

    const getCallbackStatus = (scheduledFor) => {
        const now = moment();
        const scheduled = moment(scheduledFor);
        const diffHours = scheduled.diff(now, 'hours', true);

        if (diffHours < 0) {
            return {
                label: "Overdue",
                color: "red",
                bgColor: "bg-red-50",
                borderColor: "border-red-200"
            };
        } else if (diffHours < 2) {
            return {
                label: "Due Soon",
                color: "orange",
                bgColor: "bg-orange-50",
                borderColor: "border-orange-200"
            };
        } else {
            return {
                label: "Upcoming",
                color: "blue",
                bgColor: "bg-blue-50",
                borderColor: "border-blue-200"
            };
        }
    };

    const getTimeUntil = (scheduledFor) => {
        const now = moment();
        const scheduled = moment(scheduledFor);
        const duration = moment.duration(scheduled.diff(now));

        if (duration.asSeconds() < 0) {
            const hours = Math.abs(Math.floor(duration.asHours()));
            const minutes = Math.abs(duration.minutes());
            return hours > 0 ? `${hours}h ${minutes}m overdue` : `${minutes}m overdue`;
        }

        const days = Math.floor(duration.asDays());
        const hours = duration.hours();
        const minutes = duration.minutes();

        if (days > 0) return `in ${days}d ${hours}h`;
        if (hours > 0) return `in ${hours}h ${minutes}m`;
        return `in ${minutes}m`;
    };

    const getRoleIcon = () => {
        if (!user?.position?.role) return <Users className="h-6 w-6 text-gray-600" />;
        if (user.position.role === 'admin') return <Home className="h-6 w-6 text-purple-600" />;
        if (user.position.role === 'floorManager') return <Building2 className="h-6 w-6 text-blue-600" />;
        return <Users className="h-6 w-6 text-green-600" />;
    };

    const getRoleTitle = () => {
        if (!user?.position?.role) return "Team Callbacks";
        if (user.position.role === 'admin') return "All Callbacks";
        if (user.position.role === 'floorManager') return "Floor Callbacks";
        return "Desk Callbacks";
    };

    const getRoleDescription = () => {
        if (!user?.position?.role) return "Manage callbacks for your team";
        if (user.position.role === 'admin') return "Manage all callbacks across the entire system";
        if (user.position.role === 'floorManager') return "Manage callbacks for all desks in your floor";
        return "Manage callbacks for agents in your desk(s)";
    };

    if (loading || !user) {
        return (
            <div className="flex items-center justify-center h-96">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                        {getRoleIcon()}
                        {getRoleTitle()}
                    </h1>
                    <p className="text-gray-600 mt-1">{getRoleDescription()}</p>
                </div>
                <Button onClick={fetchCallbacks} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Total Pending</p>
                                    <p className="text-2xl font-bold">{stats.pending}</p>
                                </div>
                                <Calendar className="h-8 w-8 text-blue-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Due Today</p>
                                    <p className="text-2xl font-bold text-orange-600">{stats.dueToday}</p>
                                </div>
                                <Clock className="h-8 w-8 text-orange-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">Overdue</p>
                                    <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                                </div>
                                <AlertCircle className="h-8 w-8 text-red-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-600">This Week</p>
                                    <p className="text-2xl font-bold text-green-600">{stats.dueThisWeek}</p>
                                </div>
                                <CalendarDays className="h-8 w-8 text-green-600" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters and View Mode Toggle */}
            <Card>
                <CardContent className="pt-6">
                    <div className="space-y-4">
                        {/* Agent Filter with Search */}
                        <div className="flex items-center gap-3">
                            <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Filter by Agent:
                            </label>
                            <div ref={dropdownRef} className="relative flex-1 max-w-md">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder={selectedAgent === "all" ? "Search agents..." : "Search agents..."}
                                        value={showAgentDropdown ? agentSearchQuery : getSelectedAgentName()}
                                        onChange={(e) => setAgentSearchQuery(e.target.value)}
                                        onFocus={() => {
                                            setShowAgentDropdown(true);
                                            if (selectedAgent !== "all") {
                                                setAgentSearchQuery("");
                                            }
                                        }}
                                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                {showAgentDropdown && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                        <div
                                            onClick={() => {
                                                setSelectedAgent("all");
                                                setAgentSearchQuery("");
                                                setShowAgentDropdown(false);
                                            }}
                                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${selectedAgent === "all" ? "bg-blue-50 font-medium" : ""}`}
                                        >
                                            All Agents ({callbacks.filter(cb => !cb.callbackSchedule.completed).length})
                                        </div>
                                        {agents
                                            .filter(agent => 
                                                agent.name.toLowerCase().includes(agentSearchQuery.toLowerCase())
                                            )
                                            .map(agent => {
                                                const agentCallbackCount = callbacks.filter(
                                                    cb => !cb.callbackSchedule.completed && cb.assignedUser?._id === agent.id
                                                ).length;
                                                return (
                                                    <div
                                                        key={agent.id}
                                                        onClick={() => {
                                                            setSelectedAgent(agent.id);
                                                            setAgentSearchQuery(agent.name);
                                                            setShowAgentDropdown(false);
                                                        }}
                                                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 ${selectedAgent === agent.id ? "bg-blue-50 font-medium" : ""}`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span>{agent.name}</span>
                                                            <span className="text-gray-500 text-xs">({agentCallbackCount})</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                )}
                            </div>
                            {selectedAgent !== "all" && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setSelectedAgent("all");
                                        setAgentSearchQuery("");
                                    }}
                                >
                                    Clear
                                </Button>
                            )}
                        </div>

                        {/* Time Filters and View Mode */}
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                    variant={filter === "all" ? "default" : "outline"}
                                    onClick={() => setFilter("all")}
                                    size="sm"
                                >
                                    All
                                </Button>
                                <Button
                                    variant={filter === "overdue" ? "default" : "outline"}
                                    onClick={() => setFilter("overdue")}
                                    size="sm"
                                    className={filter === "overdue" ? "bg-red-600 hover:bg-red-700 text-white" : ""}
                                >
                                    <AlertCircle className="h-3 w-3 mr-1" />
                                    Overdue
                                </Button>
                                <Button
                                    variant={filter === "today" ? "default" : "outline"}
                                    onClick={() => setFilter("today")}
                                    size="sm"
                                    className={filter === "today" ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
                                >
                                    Today
                                </Button>
                                <Button
                                    variant={filter === "tomorrow" ? "default" : "outline"}
                                    onClick={() => setFilter("tomorrow")}
                                    size="sm"
                                >
                                    Tomorrow
                                </Button>
                                <Button
                                    variant={filter === "week" ? "default" : "outline"}
                                    onClick={() => setFilter("week")}
                                    size="sm"
                                >
                                    This Week
                                </Button>
                            </div>
                            
                            {/* View Mode Toggle */}
                            <div className="flex items-center gap-2 border rounded-lg p-1">
                                <Button
                                    variant={viewMode === "list" ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setViewMode("list")}
                                >
                                    <List className="h-4 w-4 mr-1" />
                                    List
                                </Button>
                                <Button
                                    variant={viewMode === "calendar" ? "default" : "ghost"}
                                    size="sm"
                                    onClick={() => setViewMode("calendar")}
                                >
                                    <CalendarDays className="h-4 w-4 mr-1" />
                                    Calendar
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Calendar View */}
            {viewMode === "calendar" && (
                <CallbackCalendar 
                    callbacks={filteredCallbacks}
                    onCallbackClick={(callback) => {
                        setSelectedLead(callback);
                        setShowLeadModal(true);
                    }}
                />
            )}

            {/* List View */}
            {viewMode === "list" && (
                <>
                    <div className="space-y-4">
                        {filteredCallbacks.length === 0 ? (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        No Callbacks Found
                                    </h3>
                                    <p className="text-gray-600">
                                        {selectedAgent !== "all" 
                                            ? `No callbacks found for the selected agent with the current filters.`
                                            : filter === "all" 
                                                ? "Your team doesn't have any scheduled callbacks at the moment."
                                                : `Your team doesn't have any ${filter} callbacks.`
                                        }
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <>
                                {paginatedCallbacks.map((callback) => {
                            const status = getCallbackStatus(callback.callbackSchedule.scheduledFor);
                            return (
                                <Card key={callback._id} className={`${status.bgColor} border ${status.borderColor}`}>
                                    <CardContent className="py-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <h3 className="text-lg font-semibold">
                                                        {callback.firstName} {callback.lastName}
                                                    </h3>
                                                    <Badge variant={status.color}>
                                                        {status.label}
                                                    </Badge>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="h-4 w-4" />
                                                        <span className="font-medium">
                                                            {moment(callback.callbackSchedule.scheduledFor).format("MMM D, YYYY h:mm A")}
                                                        </span>
                                                        <span className="text-gray-500">
                                                            ({getTimeUntil(callback.callbackSchedule.scheduledFor)})
                                                        </span>
                                                    </div>

                                                    {callback.callbackSchedule.scheduledBy && (
                                                        <div className="flex items-center gap-2">
                                                            <User className="h-4 w-4" />
                                                            <span>
                                                                Scheduled by: {callback.callbackSchedule.scheduledBy.firstName} {callback.callbackSchedule.scheduledBy.lastName}
                                                            </span>
                                                        </div>
                                                    )}

                                                    {/* Only show phone number for managers and admins */}
                                                    {user?.position?.role && ['admin', 'floorManager', 'deskManager'].includes(user.position.role) && (
                                                        <div className="flex items-center gap-2">
                                                            <Phone className="h-4 w-4" />
                                                            <span>{callback.phone}</span>
                                                        </div>
                                                    )}

                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">Lead ID:</span>
                                                        <span className="text-gray-500">#{callback._id.slice(-4)}</span>
                                                    </div>

                                                    {callback.assignedUser && (
                                                        <div className="flex items-center gap-2">
                                                            <Users className="h-4 w-4" />
                                                            <span>
                                                                Assigned to: {callback.assignedUser.name}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {callback.callbackSchedule.note && (
                                                    <div className="mt-3 p-3 bg-white/50 rounded-lg text-sm">
                                                        <p className="font-medium text-gray-700 mb-1">Note:</p>
                                                        <p className="text-gray-600">{callback.callbackSchedule.note}</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                <Button
                                                    onClick={() => {
                                                        setSelectedLead(callback);
                                                        setShowLeadModal(true);
                                                    }}
                                                    size="sm"
                                                    variant="outline"
                                                >
                                                    <Phone className="h-4 w-4 mr-1" />
                                                    View Lead
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                        
                        {/* Pagination Controls */}
                        {filteredCallbacks.length > ITEMS_PER_PAGE && (
                            <Card>
                                <CardContent className="py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm text-gray-600">
                                            Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredCallbacks.length)} of {filteredCallbacks.length} callbacks
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                                disabled={currentPage === 1}
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                                Previous
                                            </Button>
                                            <div className="flex items-center gap-1">
                                                {Array.from({ length: Math.ceil(filteredCallbacks.length / ITEMS_PER_PAGE) }, (_, i) => i + 1).map(page => (
                                                    <Button
                                                        key={page}
                                                        variant={currentPage === page ? "default" : "outline"}
                                                        size="sm"
                                                        onClick={() => handlePageChange(page)}
                                                        className="min-w-[40px]"
                                                    >
                                                        {page}
                                                    </Button>
                                                ))}
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                                disabled={currentPage === Math.ceil(filteredCallbacks.length / ITEMS_PER_PAGE)}
                                            >
                                                Next
                                                <ChevronRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </>
                    )}
                </div>
            </>
            )}

            {/* Lead Modal */}
            {showLeadModal && selectedLead && (
                <VoiceCallModal
                    open={showLeadModal}
                    onClose={() => {
                        setShowLeadModal(false);
                        setSelectedLead(null);
                        fetchCallbacks();
                        fetchStats();
                    }}
                    lead={selectedLead}
                />
            )}
        </div>
    );
}

